import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { customerCatalogErrorMessage } from './storefront/customerFacingErrors';
import {
  buildAllProducts,
  mergeTaxonomyFromApi,
  normalizeCategoryList,
  normalizeProductSlugKeys,
} from './catalogNormalize';

const EMPTY_PRODUCTS = {};
const CATALOG_CACHE_KEY = 'saloni_catalog_cache_v2';
const CATALOG_CACHE_TTL_MS = 5 * 60 * 1000;

function isValidCatalogPayload(payload) {
  const mergedProducts = payload?.mergedProducts;
  return (
    mergedProducts &&
    typeof mergedProducts === 'object' &&
    !Array.isArray(mergedProducts) &&
    Object.keys(mergedProducts).length > 0
  );
}

function readCatalogCache() {
  try {
    const raw = sessionStorage.getItem(CATALOG_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > CATALOG_CACHE_TTL_MS) return null;
    if (!isValidCatalogPayload(parsed.payload)) return null;
    return parsed.payload;
  } catch {
    return null;
  }
}

function writeCatalogCache(payload) {
  try {
    sessionStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify({ ts: Date.now(), payload }));
  } catch {
    /* quota / private mode */
  }
}

function catalogStateFromPayload(payload) {
  if (!isValidCatalogPayload(payload)) {
    return {
      products: EMPTY_PRODUCTS,
      categories: [],
      allProducts: [],
      catalogDbActive: false,
      dataSource: 'none',
      error: null,
      loading: true,
    };
  }
  const { mergedProducts, base, dataSource } = payload;
  return {
    products: mergedProducts,
    categories: normalizeCategoryList(base, mergedProducts),
    allProducts: buildAllProducts(mergedProducts),
    catalogDbActive: true,
    dataSource: dataSource || 'mysql',
    error: null,
    loading: false,
  };
}

function initialCatalogState() {
  const cached = readCatalogCache();
  if (!cached) {
    return {
      products: EMPTY_PRODUCTS,
      categories: [],
      allProducts: [],
      catalogDbActive: false,
      dataSource: 'none',
      error: null,
      loading: true,
    };
  }
  return catalogStateFromPayload(cached);
}

function parseCatalogResponse(text) {
  const trimmed = String(text || '')
    .replace(/^\uFEFF/, '')
    .trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    const i = trimmed.indexOf('{');
    const j = trimmed.lastIndexOf('}');
    if (i >= 0 && j > i) {
      return JSON.parse(trimmed.slice(i, j + 1));
    }
    throw new Error('invalid-json');
  }
}

function isMysqlCatalogPayload(data) {
  return (
    data &&
    data.products &&
    typeof data.products === 'object' &&
    !Array.isArray(data.products) &&
    (data.ok === true ||
      data.ok === 1 ||
      data.ok === 'true' ||
      data.success === true ||
      data.success === 1)
  );
}

// Many components use useProducts() at once (header, admin, footer…). Without this,
// each mount fires its own catalog request — a burst that the single-threaded PHP dev
// server drops as "Failed to fetch". Collapse concurrent fetches to one shared request.
const inFlightCatalogRequests = new Map();
const CATALOG_FETCH_RETRIES = 3;
const CATALOG_REFETCH_COOLDOWN_MS = 20_000;
const CATALOG_LOAD_ERROR = 'We could not load the shop right now. Please refresh or try again.';
const CATALOG_REFRESH_ERROR = 'We could not refresh the catalog. Please try again.';

function isRetryableCatalogFetchError(err) {
  const msg = String(err?.message || err || '').toLowerCase();
  return (
    err instanceof TypeError ||
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('load failed')
  );
}

async function fetchCatalogResponse(url, opts) {
  let lastErr;
  for (let attempt = 0; attempt < CATALOG_FETCH_RETRIES; attempt += 1) {
    try {
      return await fetch(url, opts);
    } catch (err) {
      lastErr = err;
      if (attempt < CATALOG_FETCH_RETRIES - 1 && isRetryableCatalogFetchError(err)) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, 250 * (attempt + 1));
        });
        continue;
      }
      throw err;
    }
  }
  throw lastErr;
}

function fetchCatalogPayload(useAdminCatalog, token) {
  const key = useAdminCatalog ? `admin:${String(token || '').trim()}` : 'public';
  const existing = inFlightCatalogRequests.get(key);
  if (existing) return existing;

  const catalogUrl = useAdminCatalog ? '/api/admin_catalog.php' : '/api/catalog.php';
  const catalogFetchOpts = useAdminCatalog
    ? {
        cache: 'no-store',
        headers: {
          Authorization: `Bearer ${String(token || '').trim()}`,
          'X-Admin-Session': String(token || '').trim(),
        },
      }
    : { cache: 'no-store' };

  const promise = (async () => {
    let r;
    try {
      r = await fetchCatalogResponse(catalogUrl, catalogFetchOpts);
    } catch (err) {
      throw new Error(customerCatalogErrorMessage(err?.message, CATALOG_LOAD_ERROR));
    }
    const text = await r.text();
    let data = null;
    try {
      data = parseCatalogResponse(text);
    } catch {
      throw new Error(
        customerCatalogErrorMessage(
          !r.ok ? `Catalog API HTTP ${r.status}.` : `Invalid JSON from catalog.`,
          CATALOG_LOAD_ERROR
        )
      );
    }

    if (!isMysqlCatalogPayload(data)) {
      const msg =
        (data && typeof data.error === 'string' && data.error) ||
        (data && !r.ok ? `Catalog API HTTP ${r.status}.` : null) ||
        'Catalog is temporarily unavailable.';
      throw new Error(customerCatalogErrorMessage(msg, CATALOG_LOAD_ERROR));
    }

    const mergedProducts = normalizeProductSlugKeys(data.products);
    const base = mergeTaxonomyFromApi(data.categories, mergedProducts);
    normalizeCategoryList(base, mergedProducts).forEach((c) => {
      if (!Array.isArray(mergedProducts[c.slug])) {
        mergedProducts[c.slug] = [];
      }
    });

    return { mergedProducts, base, dataSource: 'mysql' };
  })();

  inFlightCatalogRequests.set(key, promise);
  promise.finally(() => {
    if (inFlightCatalogRequests.get(key) === promise) {
      inFlightCatalogRequests.delete(key);
    }
  });
  return promise;
}

export function useProducts() {
  const location = useLocation();
  const { ready, isAdmin, token } = useAuth();
  const loadGenRef = useRef(0);
  const lastCatalogFetchRef = useRef(0);
  const boot = initialCatalogState();
  const hasCatalogRef = useRef(boot.catalogDbActive);

  const [allProducts, setAllProducts] = useState(boot.allProducts);
  const [products, setProducts] = useState(boot.products);
  const [categories, setCategories] = useState(boot.categories);
  const [catalogDbActive, setCatalogDbActive] = useState(boot.catalogDbActive);
  const [dataSource, setDataSource] = useState(boot.dataSource);
  const [loading, setLoading] = useState(boot.loading);
  const [error, setError] = useState(boot.error);

  const adminCatalogRoute = location.pathname.startsWith('/admin');

  const loadCatalog = useCallback(async ({ silent = false } = {}) => {
    const gen = ++loadGenRef.current;
    const showLoading = !silent && !hasCatalogRef.current;
    if (showLoading) {
      setLoading(true);
    }
    if (!silent || !hasCatalogRef.current) {
      setError(null);
    }

    const isStale = () => gen !== loadGenRef.current;

    try {
      const useAdminCatalog =
        adminCatalogRoute &&
        ready &&
        isAdmin &&
        String(token || '').trim() !== '';

      const payload = await fetchCatalogPayload(useAdminCatalog, token);
      lastCatalogFetchRef.current = Date.now();

      if (!isStale()) {
        const next = catalogStateFromPayload(payload);
        setProducts(next.products);
        setCategories(next.categories);
        setAllProducts(next.allProducts);
        setCatalogDbActive(next.catalogDbActive);
        setDataSource(next.dataSource);
        setError(null);
        writeCatalogCache(payload);
        hasCatalogRef.current = true;
      }
    } catch (e) {
      if (!isStale()) {
        if (!hasCatalogRef.current) {
          setProducts(EMPTY_PRODUCTS);
          setCategories([]);
          setAllProducts([]);
          setCatalogDbActive(false);
          setDataSource('none');
          setError(
            customerCatalogErrorMessage(e.message, CATALOG_LOAD_ERROR)
          );
        } else if (!silent) {
          setError(customerCatalogErrorMessage(e.message, CATALOG_REFRESH_ERROR));
        }
      }
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  }, [adminCatalogRoute, ready, isAdmin, token]);

  const requestCatalogLoad = useCallback(
    (options = {}) => {
      void loadCatalog(options).catch(() => {});
    },
    [loadCatalog]
  );

  useEffect(() => {
    requestCatalogLoad({ silent: hasCatalogRef.current });
  }, [requestCatalogLoad]);

  useEffect(() => {
    const onCatalogUpdated = () => {
      requestCatalogLoad({ silent: hasCatalogRef.current });
    };
    window.addEventListener('saloni-catalog-updated', onCatalogUpdated);
    return () => window.removeEventListener('saloni-catalog-updated', onCatalogUpdated);
  }, [requestCatalogLoad]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastCatalogFetchRef.current < CATALOG_REFETCH_COOLDOWN_MS) return;
      requestCatalogLoad({ silent: hasCatalogRef.current });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [requestCatalogLoad]);

  return {
    allProducts,
    products,
    categories,
    catalogDbActive,
    dataSource,
    loading,
    error,
    refetch: loadCatalog,
  };
}
